import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function ShiftGroups() {
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "", member_ids: [] });

    const load = () => {
        api.get("/employee-groups").then((r) => setGroups(r.data.items || [])).catch(() => setGroups([]));
        api.get("/hris/employees", { params: { limit: 1000 } })
            .then((r) => setEmployees(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => setEmployees([]));
    };
    useEffect(() => { load(); }, []);

    const open = (g) => {
        setEditing(g || "new");
        setForm(g ? { name: g.name, description: g.description || "", member_ids: g.member_ids || [] }
                  : { name: "", description: "", member_ids: [] });
    };

    const save = async () => {
        try {
            if (editing === "new") await api.post("/employee-groups", form);
            else await api.put(`/employee-groups/${editing.id}`, form);
            toast.success("Group saved"); setEditing(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (g) => {
        if (!window.confirm(`Delete group "${g.name}"?`)) return;
        try { await api.delete(`/employee-groups/${g.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const toggle = (id) => setForm((f) => ({ ...f, member_ids:
        f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] }));

    return (
        <Surface className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                    For the crews the org chart cannot express — a night rotation drawn from
                    several departments. A group beats a department in the precedence ladder.
                </div>
                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                        onClick={() => open(null)} data-testid="new-group-btn"><Plus className="size-4" /> New group</button>
            </div>

            <div className="grid gap-2 md:grid-cols-3">
                {groups.map((g) => (
                    <div key={g.id} className="rounded-lg border p-3" data-testid={`group-${g.id}`}>
                        <div className="flex items-center justify-between">
                            <b className="text-sm">{g.name}</b>
                            <span className="badge badge-info">{g.member_count} member(s)</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{g.description || "—"}</div>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                            <button className="text-primary" onClick={() => open(g)}>Edit</button>
                            <button className="text-destructive ml-auto" onClick={() => remove(g)}>Delete</button>
                        </div>
                    </div>
                ))}
                {!groups.length && <Empty title="No groups yet" subtitle="Create one to assign a shift across departments." />}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing === "new" ? "New group" : `Edit ${form.name}`}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name</Label><Input value={form.name} data-testid="group-name"
                            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Night Crew" /></div>
                        <div><Label>Description</Label><Input value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                        <div>
                            <Label>Members ({form.member_ids.length})</Label>
                            <div className="space-y-1 max-h-64 overflow-y-auto border rounded-md p-2 mt-1">
                                {employees.map((e) => (
                                    <label key={e.id} className="flex items-center gap-2 text-sm py-0.5">
                                        <input type="checkbox" checked={form.member_ids.includes(e.id)}
                                               onChange={() => toggle(e.id)} />
                                        {e.first_name} {e.last_name}
                                        <span className="text-xs text-muted-foreground">({e.employee_no})</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm"
                                onClick={save} data-testid="group-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Surface>
    );
}
